import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const CookieBanner = () => {
    const { t } = useLanguage();
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settings, setSettings] = useState({
        necessary: true,
        analytics: false,
        functional: false,
        thirdParty: false
    });

    useEffect(() => {
        // Check if user has already made a choice
        const consent = getCookie('cookie_consent');
        if (!consent) {
            // Show banner after 1 second
            setTimeout(() => setShowBanner(true), 1000);
        } else {
            // Load cookies based on consent
            const consentData = JSON.parse(consent);
            setSettings(consentData);
            loadCookies(consentData);
        }
    }, []);

    const getCookie = (name) => {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    };

    const setCookie = (name, value, days) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    };

    const loadCookies = (consent) => {
        console.log('🔒 Loading necessary cookies...');
        
        if (consent.analytics) {
            console.log('📊 Loading Google Analytics...');
            loadGoogleAnalytics();
        }
        
        if (consent.functional) {
            console.log('🔧 Loading functional cookies...');
        }
        
        if (consent.thirdParty) {
            console.log('🌐 Loading third-party cookies...');
        }
    };

    const loadGoogleAnalytics = () => {
        // Only load if not already loaded
        if (window.gtag) return;
        
        const gaId = 'G-XXXXXXXXXX'; // Replace with your GA4 ID
        
        // Don't load if GA ID is placeholder or empty
        if (!gaId || gaId === 'G-XXXXXXXXXX' || gaId.includes('XXX')) {
            console.log('⚠️ Google Analytics skipped - No valid tracking ID configured');
            return;
        }
        
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(script);
        
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', gaId, {
            'anonymize_ip': true
        });
        
        console.log('✅ Google Analytics loaded with IP anonymization');
    };

    const acceptAll = () => {
        const consent = {
            necessary: true,
            analytics: true,
            functional: true,
            thirdParty: true
        };
        setCookie('cookie_consent', JSON.stringify(consent), 365);
        setSettings(consent);
        setShowBanner(false);
        loadCookies(consent);
    };

    const rejectAll = () => {
        const consent = {
            necessary: true,
            analytics: false,
            functional: false,
            thirdParty: false
        };
        setCookie('cookie_consent', JSON.stringify(consent), 365);
        setSettings(consent);
        setShowBanner(false);
    };

    const saveCustom = () => {
        setCookie('cookie_consent', JSON.stringify(settings), 365);
        setShowSettings(false);
        setShowBanner(false);
        loadCookies(settings);
    };

    if (!showBanner && !showSettings) return null;

    return (
        <>
            {/* Cookie Banner */}
            {showBanner && (
                <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-900 to-blue-700 text-white p-5 shadow-2xl z-50 animate-slideUp">
                    <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
                        <div className="flex-1 min-w-[300px]">
                            <h3 className="text-xl font-bold mb-2">🍪 {t('cookiesTitle')}</h3>
                            <p className="text-sm opacity-90">
                                {t('cookiesFullDesc')}
                                <br />
                                <a href="/COOKIE_POLICY.md" target="_blank" className="underline text-yellow-300 hover:text-yellow-100">
                                    {t('cookiePolicy')}
                                </a> | 
                                <a href="/PRIVACY_POLICY.md" target="_blank" className="underline text-yellow-300 hover:text-yellow-100 ml-1">
                                    {t('privacyPolicy')}
                                </a>
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button 
                                onClick={acceptAll}
                                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold transition-all hover:scale-105"
                            >
                                ✓ {t('acceptAll')}
                            </button>
                            <button 
                                onClick={rejectAll}
                                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold transition-all hover:scale-105"
                            >
                                ✗ {t('reject')}
                            </button>
                            <button 
                                onClick={() => setShowSettings(true)}
                                className="px-6 py-3 bg-transparent border-2 border-white hover:bg-white hover:text-blue-900 text-white rounded-lg font-bold transition-all"
                            >
                                ⚙ {t('settings')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            {showSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold text-blue-900 mb-4">⚙️ {t('cookieSettings')}</h2>
                        <p className="text-gray-600 mb-6">{t('cookiesDesc')}</p>

                        {/* Necessary */}
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-gray-900">
                                        {t('necessaryCookies')} <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded">{t('required')}</span>
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">{t('necessaryCookiesDesc')}</p>
                                </div>
                                <input 
                                    type="checkbox" 
                                    checked 
                                    disabled 
                                    className="w-12 h-6 mt-1"
                                />
                            </div>
                        </div>

                        {/* Analytics */}
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-gray-900">{t('analyticsCookies')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">{t('analyticsCookiesDesc')}</p>
                                </div>
                                <label className="relative inline-block w-12 h-6">
                                    <input 
                                        type="checkbox" 
                                        checked={settings.analytics}
                                        onChange={(e) => setSettings({...settings, analytics: e.target.checked})}
                                        className="sr-only peer"
                                    />
                                    <span className="absolute cursor-pointer inset-0 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-all"></span>
                                    <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></span>
                                </label>
                            </div>
                        </div>

                        {/* Functional */}
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-gray-900">{t('functionalCookies')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">{t('functionalCookiesDesc')}</p>
                                </div>
                                <label className="relative inline-block w-12 h-6">
                                    <input 
                                        type="checkbox" 
                                        checked={settings.functional}
                                        onChange={(e) => setSettings({...settings, functional: e.target.checked})}
                                        className="sr-only peer"
                                    />
                                    <span className="absolute cursor-pointer inset-0 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-all"></span>
                                    <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></span>
                                </label>
                            </div>
                        </div>

                        {/* Third Party */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-gray-900">{t('thirdPartyCookies')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">{t('thirdPartyCookiesDesc')}</p>
                                </div>
                                <label className="relative inline-block w-12 h-6">
                                    <input 
                                        type="checkbox" 
                                        checked={settings.thirdParty}
                                        onChange={(e) => setSettings({...settings, thirdParty: e.target.checked})}
                                        className="sr-only peer"
                                    />
                                    <span className="absolute cursor-pointer inset-0 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-all"></span>
                                    <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></span>
                                </label>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button 
                                onClick={saveCustom}
                                className="flex-1 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold transition-all"
                            >
                                ✓ {t('saveSettings')}
                            </button>
                            <button 
                                onClick={() => setShowSettings(false)}
                                className="flex-1 px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-bold transition-all"
                            >
                                ✗ {t('cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                .animate-slideUp {
                    animation: slideUp 0.4s ease-out;
                }
            `}</style>
        </>
    );
};

export default CookieBanner;
